import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { FileTextIcon } from 'lucide-react'
import { useId } from 'react'

import { ApiError } from '@/api/errors'
import { portalApi } from '@/api/portal'
import type { QuoteStatus } from '@/api/types'
import { EmptyState } from '@/components/empty-state'
import { FilterBar, FilterField } from '@/components/filter-bar'
import { Frame } from '@/components/frame'
import { PageHeader } from '@/components/page-header'
import { QuoteStatusBadge } from '@/components/quotes/quote-status-badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { msg } from '@/i18n'
import { formatDate, formatDateOnly } from '@/lib/format'
import { QUOTE_STATUS_LABELS } from '@/lib/labels'
import { formatMoney } from '@/lib/money'
import { oneOf, readSearch } from '@/lib/search-params'

/** Ce que le portail montre : jamais un brouillon, une retirée ni une remplacée. */
const PORTAL_STATUSES = [
  'sent',
  'accepted',
  'declined',
  'expired',
] as const satisfies readonly QuoteStatus[]
const ALL = '__toutes__'

const SHAPE = {
  statut: oneOf(PORTAL_STATUSES),
}

/** Espace client › Soumissions (lot 14, #208 tranche d) : ce qui vous a été proposé. */
export const Route = createFileRoute('/portal/soumissions/')({
  validateSearch: (search: Record<string, unknown>) => readSearch(SHAPE, search),
  component: PortalQuotesPage,
})

function PortalQuotesPage() {
  const t = msg().portalForms.quotes
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const id = useId()
  const params = search.statut ? { status: search.statut } : {}
  const quotes = useQuery({
    queryKey: ['portal', 'quotes', params],
    queryFn: () => portalApi.quotes(params),
    placeholderData: (previous) => previous,
  })

  return (
    <div className="space-y-6">
      <PageHeader title={t.title} documentTitle={t.title} />
      <Frame>
        <FilterBar
          active={
            search.statut
              ? [
                  {
                    key: 'statut',
                    label: QUOTE_STATUS_LABELS[search.statut],
                    onClear: () => {
                      void navigate({ search: {} })
                    },
                  },
                ]
              : []
          }
          panelTitle={t.filterTitle}
        >
          <FilterField label={t.status} htmlFor={`${id}-statut`}>
            <Select
              value={search.statut ?? ALL}
              onValueChange={(value) => {
                void navigate({
                  search:
                    value === ALL ? {} : { statut: value as (typeof PORTAL_STATUSES)[number] },
                })
              }}
            >
              <SelectTrigger id={`${id}-statut`} aria-label={t.filterByStatus}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t.allStatuses}</SelectItem>
                {PORTAL_STATUSES.map((status) => (
                  <SelectItem key={status} value={status}>
                    {QUOTE_STATUS_LABELS[status]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
        </FilterBar>

        {quotes.isPending ? (
          <div className="space-y-2" aria-busy="true">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : quotes.isError ? (
          <EmptyState
            icon={FileTextIcon}
            title={t.loadFailedTitle}
            description={quotes.error instanceof ApiError ? quotes.error.message : t.retryLater}
            action={
              <Button
                variant="outline"
                onClick={() => {
                  void quotes.refetch()
                }}
              >
                {t.retry}
              </Button>
            }
          />
        ) : quotes.data.length === 0 ? (
          <EmptyState
            icon={FileTextIcon}
            title={search.statut ? t.emptyFilteredTitle : t.emptyTitle}
            description={search.statut ? t.emptyFiltered : t.empty}
          />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  <TableHead>{t.columns.number}</TableHead>
                  <TableHead>{t.columns.title}</TableHead>
                  <TableHead>{t.columns.status}</TableHead>
                  <TableHead className="text-right">{t.columns.total}</TableHead>
                  <TableHead>{t.columns.validUntil}</TableHead>
                  <TableHead>{t.columns.received}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quotes.data.map((quote) => (
                  <TableRow
                    key={quote.id}
                    data-testid="soumission-portail-ligne"
                    data-numero={quote.number}
                  >
                    <TableCell className="font-key whitespace-nowrap">
                      <Link
                        to="/portal/soumissions/$quoteId"
                        params={{ quoteId: quote.id }}
                        className="rounded-sm font-medium hover:underline focus-visible:outline-2 focus-visible:outline-ring"
                      >
                        {quote.number}
                      </Link>
                    </TableCell>
                    <TableCell className="max-w-[24rem] truncate">{quote.title}</TableCell>
                    <TableCell>
                      <QuoteStatusBadge status={quote.status} />
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap tabular-nums">
                      {formatMoney(quote.total_cents)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {quote.valid_until ? formatDateOnly(quote.valid_until) : '—'}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {quote.sent_at ? formatDate(quote.sent_at) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Frame>
    </div>
  )
}
